import { useEffect, useState, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const SEAT_TIMEOUT_MS = 5 * 60 * 1000; // 5 dakika — SeatHub ile aynı

export const useSeating = (eventId, onExternalSeatReleased) => {
  const [connection, setConnection]   = useState(null);
  const [seats, setSeats]             = useState({});
  const [mySeats, setMySeats]         = useState(new Set());
  const [isConnected, setIsConnected] = useState(false);

  // Her seçili koltuk için kalan süre: { seatId: secondsLeft }
  const [seatTimers, setSeatTimers]   = useState({});

  const connectionRef             = useRef(null);
  const onExternalSeatReleasedRef = useRef(onExternalSeatReleased);
  const seatExpiryRef             = useRef({}); // { seatId: expiresAt (ms) }
  const timerIntervalRef          = useRef(null);

  useEffect(() => {
    onExternalSeatReleasedRef.current = onExternalSeatReleased;
  }, [onExternalSeatReleased]);

  // Saniye bazlı countdown ticker
  useEffect(() => {
    timerIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const updated = {};
      let hasAny = false;
      Object.entries(seatExpiryRef.current).forEach(([seatId, expiresAt]) => {
        const remaining = Math.max(0, Math.ceil((expiresAt - now) / 1000));
        updated[seatId] = remaining;
        hasAny = true;
      });
      if (hasAny) setSeatTimers({ ...updated });
    }, 1000);

    return () => clearInterval(timerIntervalRef.current);
  }, []);

  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/seathub`)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connectionRef.current = newConnection;
    setConnection(newConnection);

    return () => {
      if (connectionRef.current) connectionRef.current.stop();
    };
  }, []);

  useEffect(() => {
    if (!connection) return;

    connection.on('InitialSeats', (initialSeats) => {
      setSeats(initialSeats || {});
    });

    connection.on('SeatSelected', (seatId, userId) => {
      setSeats(prev => ({ ...prev, [seatId]: { userId, status: 'temp' } }));
    });

    connection.on('SeatConfirmed', (seatId, userId) => {
      setSeats(prev => ({ ...prev, [seatId]: { userId, status: 'confirmed' } }));
    });

    connection.on('SeatReleased', (seatId) => {
      setSeats(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      setMySeats(prev => { const s = new Set(prev); s.delete(seatId); return s; });
      // Timer temizle
      delete seatExpiryRef.current[seatId];
      setSeatTimers(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      onExternalSeatReleasedRef.current?.(seatId);
    });

    connection.onreconnecting(() => setIsConnected(false));
    connection.onreconnected(() => {
      setIsConnected(true);
      connection.invoke('JoinEvent', eventId);
    });
    connection.onclose(() => setIsConnected(false));

    connection.start()
      .then(() => {
        setIsConnected(true);
        return connection.invoke('JoinEvent', eventId);
      })
      .catch(err => {
        console.error('SignalR bağlantı hatası:', err);
        setIsConnected(false);
      });
  }, [connection, eventId]);

  const selectSeat = useCallback(async (seatId) => {
    if (!connection || !isConnected) return false;
    try {
      const success = await connection.invoke('SelectSeat', eventId, seatId);
      if (success) {
        setMySeats(prev => new Set(prev).add(seatId));
        // Timer başlat
        seatExpiryRef.current[seatId] = Date.now() + SEAT_TIMEOUT_MS;
        setSeatTimers(prev => ({ ...prev, [seatId]: Math.ceil(SEAT_TIMEOUT_MS / 1000) }));
      }
      return success;
    } catch (err) {
      console.error('Koltuk seçme hatası:', err);
      return false;
    }
  }, [connection, eventId, isConnected]);

  const confirmSeat = useCallback(async (seatId) => {
    if (!connection || !isConnected) return;
    try {
      await connection.invoke('ConfirmSeat', eventId, seatId);
      // Timer iptal et — onaylandı
      delete seatExpiryRef.current[seatId];
      setSeatTimers(prev => { const n = { ...prev }; delete n[seatId]; return n; });
    } catch (err) {
      console.error('Koltuk onaylama hatası:', err);
    }
  }, [connection, eventId, isConnected]);

  const releaseSeat = useCallback(async (seatId) => {
    if (!connection || !isConnected) return false;
    try {
      await connection.invoke('ReleaseSeat', eventId, seatId);
      setMySeats(prev => { const s = new Set(prev); s.delete(seatId); return s; });
      setSeats(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      delete seatExpiryRef.current[seatId];
      setSeatTimers(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      return true;
    } catch (err) {
      console.error('Koltuk bırakma hatası:', err);
      return false;
    }
  }, [connection, eventId, isConnected]);

  // En az kalan süreyi döndür (en az zamanı kalan koltuk)
  const minSecondsLeft = Object.values(seatTimers).length > 0
    ? Math.min(...Object.values(seatTimers))
    : null;

  return {
    seats, mySeats, selectSeat, confirmSeat, releaseSeat, isConnected,
    seatTimers,      // { seatId: secondsLeft }
    minSecondsLeft,  // en kritik kalan süre (gösterim için)
  };
};